import type { TemplateOptionDef } from '@/types/templateMeta';

export const CONTACT_METHOD_FIELD = 'form_contact_method';

export interface PublicationFieldCatalogEntry {
  key: string;
  input_type: string;
  label?: string;
  description?: string;
  required?: boolean;
  editable: true;
  tab: 'form' | 'settings';
  section?: string;
  enum?: string[];
  forces_refresh?: boolean;
  schema?: Record<string, unknown>;
}

export function isFormSection(header: TemplateOptionDef | null): boolean {
  // POPS templates are mid-migration from declaring this section's title as "Funnel Form"/
  // "Funnel-Formular" to the new "Form Text"/"Formular-Text" - match both so a template bundle
  // built before or after the rename is detected correctly. Retire the old spellings once every
  // template bundle has redeployed and caches have expired.
  const title = header?.properties.title?.toLocaleLowerCase();
  return title === 'funnel form' || title === 'funnel-formular' || title === 'form text' || title === 'formular-text';
}

export function isFormTemplateOption(def: TemplateOptionDef, header: TemplateOptionDef | null): boolean {
  return isFormSection(header) || !!def.properties.is_form_option || !!def.properties.is_form_required_option;
}

/**
 * Flattens a template's option defs into the catalog published at
 * `context.current_publication.fields`. Section headers are skipped; fields without a key are
 * skipped. Form vs settings follows the same rules as the editor's left-pane split.
 */
export function describeTemplateFields(options: TemplateOptionDef[] | undefined): PublicationFieldCatalogEntry[] {
  const fields: PublicationFieldCatalogEntry[] = [];
  let currentHeader: TemplateOptionDef | null = null;

  for (const def of options ?? []) {
    if (def.type === 'section.header') {
      currentHeader = def;
      continue;
    }

    const key = def.properties.key;
    if (!key) continue;

    const tab: 'form' | 'settings' = isFormTemplateOption(def, currentHeader) ? 'form' : 'settings';
    const selectValues = (def.properties.options ?? [])
      .map((opt) => opt.value)
      .filter((value): value is string => typeof value === 'string');

    const entry: PublicationFieldCatalogEntry = {
      key,
      input_type: def.type,
      editable: true,
      tab,
    };

    if (def.properties.label) entry.label = def.properties.label;
    if (def.properties.description) entry.description = def.properties.description;
    if (def.properties.required) entry.required = true;
    if (currentHeader?.properties.title) entry.section = currentHeader.properties.title;
    if (selectValues.length) entry.enum = selectValues;
    if (def.properties.forces_refresh) entry.forces_refresh = true;
    if (def.properties.schema) {
      entry.schema = normalizeTemplateJsonSchema(def.properties.schema) as Record<string, unknown>;
    }

    fields.push(entry);
  }

  return fields;
}

function fieldDescription(field: PublicationFieldCatalogEntry): string {
  return [field.label, field.description].filter(Boolean).join(' — ') || field.key;
}

/** JSON Schema keywords whose values are maps of name → schema, not arrays. */
const SCHEMA_MAP_KEYS = new Set(['properties', 'patternProperties', '$defs', 'definitions']);

/** True when `value` is a JSON object that is really a 0- or 1-indexed array (a Lua table). */
function isLuaIndexedArray(value: Record<string, unknown>): boolean {
  const keys = Object.keys(value);
  if (keys.length === 0) return false;

  const indexes: number[] = [];
  for (const key of keys) {
    if (!/^[0-9]+$/.test(key)) return false;
    indexes.push(Number(key));
  }
  indexes.sort((a, b) => a - b);

  const start = indexes[0];
  if (start !== 0 && start !== 1) return false;
  return indexes.every((index, i) => index === start + i);
}

/**
 * Rewrite Lua 1-indexed tables nested in a template JSON Schema into real JSON arrays.
 * `required = { 'text' }` arrives as `{ '1': 'text' }`; OpenAI rejects that as not an array.
 */
export function normalizeTemplateJsonSchema(value: unknown, parentKey?: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeTemplateJsonSchema(item));
  }
  if (!value || typeof value !== 'object') return value;

  const obj = value as Record<string, unknown>;
  const keepAsMap = parentKey !== undefined && SCHEMA_MAP_KEYS.has(parentKey);
  if (!keepAsMap && isLuaIndexedArray(obj)) {
    return Object.values(obj).map((item) => normalizeTemplateJsonSchema(item));
  }

  const result: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(obj)) {
    result[key] = normalizeTemplateJsonSchema(nested, key);
  }
  return result;
}

/** JSON-schema property for one catalog field, used as a `set_fields` parameter. */
export function fieldToJsonSchema(field: PublicationFieldCatalogEntry): Record<string, unknown> {
  const description = fieldDescription(field);
  switch (field.input_type) {
    case 'input.checkbox':
      return { type: 'boolean', description };
    case 'input.multiselect':
      return {
        type: 'array',
        items: field.enum?.length ? { type: 'string', enum: field.enum } : { type: 'string' },
        description,
      };
    case 'input.select':
      return field.enum?.length ? { type: 'string', enum: field.enum, description } : { type: 'string', description };
    case 'input.image':
      return {
        description: `${description}. Pass { "hash": "<company image hash>" }, the hash string, or null to clear.`,
      };
    case 'input.complex':
      return field.schema
        ? { ...(normalizeTemplateJsonSchema(field.schema) as Record<string, unknown>), description }
        : { description };
    default:
      return { type: 'string', description };
  }
}

/**
 * Parameter properties for `current_publication_set_fields`. Catalog fields are typed; any extra
 * key already present in the live form is listed too so the model will actually pass it (an empty
 * `additionalProperties` object is not enough — models treat undocumented keys as invalid).
 */
export function setFieldsJsonSchemaProperties(
  fields: PublicationFieldCatalogEntry[],
  form: Record<string, unknown> | undefined
): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    [CONTACT_METHOD_FIELD]: {
      type: 'string',
      enum: ['email', 'phone', 'both'],
      description: 'Candidate contact method.',
    },
  };

  for (const field of fields) {
    properties[field.key] = fieldToJsonSchema(field);
  }

  for (const key of Object.keys(form ?? {})) {
    if (properties[key]) continue;
    properties[key] = {
      description: `Live value in context.current_publication.form.${key}. Writable — pass the replacement value.`,
    };
  }

  return properties;
}
